// src/services/socialService.js
// Backs the Teammates directory (public-profile browsing + connection
// requests).
// Kept separate from profileService.js, which owns the fully-private
// profile document — this file only ever touches the narrow public slice.
import { db } from '../config/firebase';
import { collection, doc, setDoc, getDocs, addDoc, updateDoc, query, where } from 'firebase/firestore';

// displayName is deliberately NOT here — real names must never be readable
// by other students (anyone signed in can read any publicProfiles doc, so
// "just don't render it in the UI" isn't enough; it can't be written here
// at all). username is the only identity shown to other students; the app
// and the AI chat can still use displayName directly off the private
// profiles/{uid} doc, which only the owning user can read.
const PUBLIC_FIELDS = ['username', 'grade', 'region', 'interests', 'skills', 'goals', 'photoURL'];

export async function savePublicProfile(uid, profile) {
  const data = {};
  PUBLIC_FIELDS.forEach(f => { data[f] = profile?.[f] ?? null; });
  try {
    await setDoc(doc(db, 'publicProfiles', uid), data, { merge: true });
  } catch (e) {
    // Non-fatal — the directory just won't include this user until it succeeds.
  }
}

export async function listPublicProfiles() {
  const snap = await getDocs(collection(db, 'publicProfiles'));
  return snap.docs.map(d => ({ uid: d.id, ...d.data() }));
}

// fromName must be a username, never a real display name — this doc is
// readable by the recipient (see firestore.rules). No email field either —
// it was stored but never actually used anywhere, pure leak risk for zero
// benefit.
export async function sendConnectionRequest(fromUid, fromName, toUid, message) {
  await addDoc(collection(db, 'connectionRequests'), {
    fromUid, fromName, toUid,
    message: message || '', status: 'pending', createdAt: new Date().toISOString(),
  });
}

export async function listIncomingRequests(uid) {
  const q = query(collection(db, 'connectionRequests'), where('toUid', '==', uid));
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

export async function listSentRequests(uid) {
  const q = query(collection(db, 'connectionRequests'), where('fromUid', '==', uid));
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

export async function respondToRequest(requestId, status) {
  await updateDoc(doc(db, 'connectionRequests', requestId), { status });
}
